import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import type {
  ManipulatedComponentInput,
  MedicalPrescriptionInput,
} from "@/lib/validations/medical-prescription";
import { Button, Checkbox, Input, SegmentedTabs } from "@akuna/ui";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MedicationAutocomplete,
  type MedicationAutocompleteExtraOption,
  type MedicationWithRelations,
} from "./MedicationAutocomplete";
import { useTranslation } from "react-i18next";

const ROUTE_OPTIONS = [
  "Oral",
  "Tópica",
  "Oftálmica",
  "Subcutânea",
  "Intramuscular",
  "Intravenosa",
  "Otológica",
  "Transdérmica",
];

interface ManipulatedPrescriptionFormProps {
  form: UseFormReturn<MedicalPrescriptionInput>;
  medications: MedicationWithRelations[];
  isLoadingMedications?: boolean;
  onSearchChange?: (value: string) => void;
  onOpenCreateDialog?: (name: string, index: number) => void;
  extraAutocompleteOptions?: MedicationAutocompleteExtraOption[];
  onSelectExtraAutocompleteOption?: (option: MedicationAutocompleteExtraOption, index: number) => void;
}

export function ManipulatedPrescriptionForm({
  form,
  medications,
  isLoadingMedications = false,
  onSearchChange,
  onOpenCreateDialog,
  extraAutocompleteOptions = [],
  onSelectExtraAutocompleteOption,
}: ManipulatedPrescriptionFormProps) {
  const { t } = useTranslation("prescriptions");
  const [componentModes, setComponentModes] = useState<Record<number, "structured" | "freeText">>({});
  const {
    fields: componentFields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: "manipulated_payload.components" as const,
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border/70 p-4 border-dashed">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-sm font-semibold">{t("manipulated.formulaTitle")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("manipulated.formulaDescription")}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {componentFields.map((field, index) => (
            <div
              key={field.id}
              className="rounded-lg border border-border/70 p-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                  <SegmentedTabs
                    value={componentModes[index] ?? "structured"}
                    onValueChange={(value) =>
                      setComponentModes((current) => ({
                        ...current,
                        [index]: value as "structured" | "freeText",
                      }))
                    }
                    fill="screen"
                    width="full"
                    items={[
                      {
                        value: "structured",
                        label: t("composer.structuredFieldsTab"),
                      },
                      {
                        value: "freeText",
                        label: t("composer.freeTextTab"),
                      },
                    ]}
                  />

                  {(componentModes[index] ?? "structured") === "structured" ? (
                    <>
                      <FormField
                        control={form.control}
                        name={`manipulated_payload.components.${index}.name` as const}
                        render={({ field: nameField }) => (
                          <FormItem>
                            <FormLabel>{t("manipulated.activeIngredient")}</FormLabel>
                            <FormControl>
                              <MedicationAutocomplete
                                popoverPortalled={false}
                                medications={medications}
                                isLoading={isLoadingMedications}
                                value={nameField.value ?? ""}
                                onFocus={() => {
                                  if (!(nameField.value ?? "").trim()) {
                                    onSearchChange?.("");
                                  }
                                }}
                                selectedMedicationId={
                                  form.watch(
                                    `manipulated_payload.components.${index}.medication_id`,
                                  ) ?? ""
                                }
                                onChange={(value) => {
                                  onSearchChange?.(value);
                                  const currentComponent = form.getValues(
                                    `manipulated_payload.components.${index}`,
                                  );
                                  if (currentComponent?.medication_id) {
                                    form.setValue(
                                      `manipulated_payload.components.${index}.medication_id`,
                                      "",
                                    );
                                  }
                                  nameField.onChange(value);
                                }}
                                onSelectMedication={(medication, displayName) => {
                                  onSearchChange?.("");
                                  nameField.onChange(displayName);
                                  form.setValue(
                                    `manipulated_payload.components.${index}.medication_id`,
                                    medication.id,
                                  );
                                  form.setValue(
                                    `manipulated_payload.components.${index}.name`,
                                    displayName,
                                  );
                                  form.clearErrors(
                                    `manipulated_payload.components.${index}.name`,
                                  );
                                }}
                                onOpenCreateDialog={
                                  onOpenCreateDialog
                                    ? (value) => onOpenCreateDialog(value, index)
                                    : undefined
                                }
                                extraOptions={extraAutocompleteOptions}
                                onSelectExtraOption={
                                  onSelectExtraAutocompleteOption
                                    ? (option) => onSelectExtraAutocompleteOption(option, index)
                                    : undefined
                                }
                                placeholder={t("manipulated.activeIngredientPlaceholder")}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`manipulated_payload.components.${index}.concentration` as const}
                        render={({ field: concentrationField }) => (
                          <FormItem>
                            <FormLabel>{t("manipulated.concentration")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("manipulated.concentrationPlaceholder")} {...concentrationField} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <FormField
                        control={form.control}
                        name={`manipulated_payload.components.${index}.name` as const}
                        render={({ field: nameField }) => (
                          <FormItem>
                            <FormLabel>{t("manipulated.activeIngredient")}</FormLabel>
                            <FormControl>
                              <MedicationAutocomplete
                                medications={medications}
                                isLoading={isLoadingMedications}
                                value={nameField.value ?? ""}
                                onFocus={() => {
                                  if (!(nameField.value ?? "").trim()) {
                                    onSearchChange?.("");
                                  }
                                }}
                                selectedMedicationId={
                                  form.watch(
                                    `manipulated_payload.components.${index}.medication_id`,
                                  ) ?? ""
                                }
                                onChange={(value) => {
                                  onSearchChange?.(value);
                                  const currentComponent = form.getValues(
                                    `manipulated_payload.components.${index}`,
                                  );
                                  if (currentComponent?.medication_id) {
                                    form.setValue(
                                      `manipulated_payload.components.${index}.medication_id`,
                                      "",
                                    );
                                  }
                                  nameField.onChange(value);
                                }}
                                onSelectMedication={(medication, displayName) => {
                                  onSearchChange?.("");
                                  nameField.onChange(displayName);
                                  form.setValue(
                                    `manipulated_payload.components.${index}.medication_id`,
                                    medication.id,
                                  );
                                  form.setValue(
                                    `manipulated_payload.components.${index}.name`,
                                    displayName,
                                  );
                                  form.clearErrors(
                                    `manipulated_payload.components.${index}.name`,
                                  );
                                }}
                                onOpenCreateDialog={
                                  onOpenCreateDialog
                                    ? (value) => onOpenCreateDialog(value, index)
                                    : undefined
                                }
                                extraOptions={extraAutocompleteOptions}
                                onSelectExtraOption={
                                  onSelectExtraAutocompleteOption
                                    ? (option) => onSelectExtraAutocompleteOption(option, index)
                                    : undefined
                                }
                                placeholder={t("manipulated.activeIngredientPlaceholder")}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`manipulated_payload.components.${index}.free_text` as const}
                        render={({ field: freeTextField }) => (
                          <FormItem>
                            <FormLabel>{t("composer.freeTextFieldLabel")}</FormLabel>
                            <FormControl>
                              <AutoGrowTextarea
                                placeholder={t("composer.freeTextFieldPlaceholder")}
                                {...freeTextField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>

                {componentFields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mt-7 shrink-0 text-muted-foreground"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full mt-4"
          onClick={() => {
            onSearchChange?.("");
            append({
              medication_id: "",
              name: "",
              concentration: "",
              free_text: "",
            } satisfies ManipulatedComponentInput);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("manipulated.addComponent")}
        </Button>
      </div>

      <div className="rounded-lg border border-border/70 p-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold">{t("manipulated.routeTitle")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("manipulated.routeDescription")}
          </p>
        </div>

        <div className="mt-4 grid gap-4">
          <FormField
            control={form.control}
            name={"manipulated_payload.route" as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("manipulated.routeTitle")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ""}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("manipulated.routePlaceholder")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ROUTE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className="rounded-lg border border-border/70 p-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold">{t("manipulated.treatmentTitle")}</h3>
          <p className="text-xs text-muted-foreground">{t("manipulated.treatmentDescription")}</p>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name={"manipulated_payload.treatment_days" as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("manipulated.treatmentDays")}</FormLabel>
                <FormControl>
                  <Input type="number" min={1} placeholder={t("manipulated.daysPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={"manipulated_payload.total_doses" as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("manipulated.totalDoses")}</FormLabel>
                <FormControl>
                  <Input type="number" min={1} placeholder={t("manipulated.dosesPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className="rounded-lg border border-border/70 p-4">
        <div className="flex items-center gap-3">
          <Checkbox
            id="mapa-controlled"
            checked={form.watch("control_regime") === "manipulated_controlled_mapa"}
            onCheckedChange={(checked) => {
              if (checked) {
                form.setValue("control_regime", "manipulated_controlled_mapa", { shouldValidate: true });
                form.setValue("validity_in_days", 30, { shouldValidate: true });
              } else {
                form.setValue("control_regime", "none", { shouldValidate: true });
              }
            }}
          />
            <Label htmlFor="mapa-controlled" className="text-sm font-medium cursor-pointer">
              {t("manipulated.mapaControlledCheckbox")}
            </Label>
        </div>
      </div>
    </div>
  );
}
